import { useCallback, useEffect, useMemo, useState } from 'react'
import { DropOffLocation } from '../../domain/models'

export interface AdditionnalFilter {
  dropOffIds?: string[]
}

export interface FilterActions {
  setWasteTypeIds: (ids: string[]) => void
  wasteTypeIds: string[]
  setBrandProgram: (str: string | null) => void
  buildContextualQuery: (additionnalFilter: AdditionnalFilter) => string
  setDropOffLocation: (dropOff: DropOffLocation | undefined) => void
  dropOffLocation?: DropOffLocation | undefined
  query: string
}

const hasAtLeastOneElementInCommon = (set1: string[], set2: string[]): boolean => {
  const principal = new Set<string>(set1)
  return !!set2.find((str) => principal.has(str))
}

type UseFilterHook = (defaultWasteTypeIds: string[]) => FilterActions

const toWasteTypeQueries = (wasteTypeIds: string[]): string[] =>
  wasteTypeIds.map((id) => `tag:"WT_${id.replaceAll(' ', '_')}"`)

const useFilter: UseFilterHook = (ids: string[]) => {
  const [wasteTypeIds, setWasteTypeIds] = useState<string[]>([])
  const [brandProgram, setBrandProgram] = useState<string | null>(null)
  const [dropOffLocation, setDropOffLocation] = useState<DropOffLocation | undefined>(undefined)
  const [query, setQuery] = useState<string>('')

  const defaultWasteTypeIds = useMemo(() => ids, [ids])

  const brandProgramQuery = useMemo(() => {
    if (brandProgram) {
      return `user.brandProgramAffiliation:=${brandProgram}`
    }
    return ''
  }, [brandProgram])

  const displayedStoreQuery = useMemo(() => {
    const filterIds = wasteTypeIds.length ? wasteTypeIds : defaultWasteTypeIds
    if (filterIds.length) {
      const wasteTypeQueries = toWasteTypeQueries(filterIds)
      dropOffLocation &&
        !hasAtLeastOneElementInCommon(filterIds, dropOffLocation.acceptedWasteIds) &&
        wasteTypeQueries.push(`idstore:="${dropOffLocation.storeId}"`)
      return `(${wasteTypeQueries.join(' OR ')})`
    }
    return ''
  }, [defaultWasteTypeIds, dropOffLocation, wasteTypeIds])

  const queries: string[] = useMemo(() => {
    return [brandProgramQuery, displayedStoreQuery].filter((str) => !!str)
  }, [brandProgramQuery, displayedStoreQuery])

  useEffect(() => {
    const newQuery = queries.join(' AND ')
    if (query !== newQuery) {
      setQuery(newQuery)
    }
  }, [queries, query])

  const defaultWasteTypesQuery = toWasteTypeQueries(defaultWasteTypeIds).join(' OR ')

  const buildContextualQuery = useCallback(
    (additionnalFilter: AdditionnalFilter): string => {
      const { dropOffIds } = additionnalFilter
      let newQueries: string[] = [brandProgramQuery, `(${defaultWasteTypesQuery})`]
      if (dropOffIds && dropOffIds.length) {
        const dropOffQuery = `(${dropOffIds.map((id) => `idstore:="${id}"`).join(' OR ')})`
        newQueries.push(dropOffQuery)
      }
      return newQueries.filter((query) => !!query).join(' AND ')
    },
    [brandProgramQuery, defaultWasteTypesQuery]
  )

  return {
    setWasteTypeIds,
    wasteTypeIds,
    setBrandProgram,
    buildContextualQuery,
    setDropOffLocation,
    dropOffLocation,
    query
  }
}

export default useFilter
